import { BULLET_RADIUS, SHIP_RADIUS, SHIP_NOSE_OFFSET, WORLD_WIDTH, WORLD_HEIGHT } from 'asteroids-shared/constants'
import { rotatePoint } from 'asteroids-shared/helpers'
import { wrap } from 'asteroids-shared/physics'
import { drawBullet } from '../renderer/BulletRenderer.js'

/**
 * localStorage `debug=1` or any `?debug` in the URL turns on rings, red interp overlay, etc.
 * Optional comma tokens on `?debug=a,b` — **`bullet`**: `[bullet-debug]` console lines for spawn pairing.
 */

const STORAGE_KEY = 'debug'
const LINE_WIDTH = 1
const CROSSHAIR = 3

// Extra beyond `asteroidRadius + BULLET_RADIUS` for debug hitbox ring radii (asteroids + ships).
export const HITBOX_DEBUG_PAD = 3

/**
 * Minimum interp `sample` lag (ms) for hitbox overlay; {@link hitboxOverlayLagSampleMs} adds
 * EMA-smoothed staleness (`serverNow` − newest snapshot) up to a cap.
 */
export const HITBOX_OVERLAY_INTERP_LAG_MS = 100

// Max `serverNow - latestServerTime` blended into overlay lag (ms).
export const HITBOX_OVERLAY_LAG_STALENESS_CAP_MS = 250
// Upper bound on `interp.sample` lag used for hitbox overlay (ms).
export const HITBOX_OVERLAY_LAG_TOTAL_CAP_MS = 380
/**
 * Exponential smoothing time scale for overlay lag (ms): larger → EMA follows measured lag more slowly.
 * Step uses `α = 1 − exp(−Δt / this)` toward {@link hitboxOverlayLagSampleMs}.
 */
export const HITBOX_OVERLAY_LAG_EMA_SMOOTHING_MS = 200

/**
 * One-shot target lag: base interp lag plus smoothed authority staleness
 * (`serverNow` − newest snapshot `serverTime`), clamped.
 * @returns {number | null} null if clocks not usable
 */
export function hitboxOverlayLagSampleMs(serverNowMs, latestServerTimeMs) {
  if (!Number.isFinite(serverNowMs) || !Number.isFinite(latestServerTimeMs)) return null
  const staleness = Math.min(
    HITBOX_OVERLAY_LAG_STALENESS_CAP_MS,
    Math.max(0, serverNowMs - latestServerTimeMs)
  )
  const total = HITBOX_OVERLAY_INTERP_LAG_MS + staleness
  return Math.min(HITBOX_OVERLAY_LAG_TOTAL_CAP_MS, total)
}

/**
 * Exponential smoothing toward `sampleMs` (same units as {@link hitboxOverlayLagSampleMs}).
 * @param {number} prevMs previous EMA (or NaN to snap to sample)
 * @param {number | null} sampleMs from {@link hitboxOverlayLagSampleMs}
 * @param {number} elapsedMs wall delta since last update
 */
export function updateHitboxOverlayLagEma(prevMs, sampleMs, elapsedMs) {
  if (sampleMs == null || !Number.isFinite(sampleMs)) return prevMs
  const dt = Math.min(100, Math.max(0, elapsedMs))
  const smoothingMs = HITBOX_OVERLAY_LAG_EMA_SMOOTHING_MS
  const alpha = smoothingMs > 0 ? 1 - Math.exp(-dt / smoothingMs) : 1
  const p = Number.isFinite(prevMs) ? prevMs : sampleMs
  return p + alpha * (sampleMs - p)
}

/**
 * Clamp `tBase - lagMs` to oldest buffered snapshot time (valid `interp.sample`).
 * @param {{ oldest: () => { serverTime: number } | null }} interp
 */
export function clampInterpLagSampleTime(interp, tBase, lagMs) {
  if (!Number.isFinite(tBase) || !Number.isFinite(lagMs)) return tBase
  const span = Math.max(0, lagMs)
  let t = tBase - span
  const oldestSt = interp.oldest()?.serverTime
  if (oldestSt != null && Number.isFinite(oldestSt) && t < oldestSt) t = oldestSt
  if (t > tBase) t = tBase
  return t
}

const locationSearchParams = () => {
  if (typeof location === 'undefined') return null
  try {
    return new URLSearchParams(location.search)
  } catch {
    return null
  }
}

export function isGameDebugEnabled() {
  const sp = locationSearchParams()
  if (sp?.has('debug')) return true
  try {
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

// Comma-separated tokens after `?debug=`; plain `?debug` or `?debug=` yields `[]`.
export function gameDebugTokens() {
  const sp = locationSearchParams()
  if (!sp) return []
  const raw = sp.get('debug')
  if (raw == null || raw === '') return []
  return raw.split(',').map((s) => s.trim()).filter(Boolean)
}

// Hitbox rings, red interp overlay, lagged bullets — same gate as `isGameDebugEnabled`.
export function isDebugEnabled() {
  return isGameDebugEnabled()
}

const logBulletDebug = (label, data) => {
  if (!isGameDebugEnabled() || !gameDebugTokens().includes('bullet')) return
  console.log(`[bullet-debug] ${label}`, data)
}

export function bulletDebugLocalSpawn(payload) {
  if (!isGameDebugEnabled()) return
  logBulletDebug('local_spawn', payload)
}

export function bulletDebugServerSpawn(payload) {
  if (!isGameDebugEnabled()) return
  logBulletDebug('server_spawn', payload)
}

/**
 * Own white slug is drawn elsewhere; this draws the red interp slug when overlay is on.
 * @param {{ shipId: number }} options
 * @returns {boolean} true if caller should skip the normal interp draw for this bullet
 */
export function localPlayerViewBulletDebug(ctx, camera, bullet, options) {
  if (bullet.ownerId !== options.shipId) return false
  if (!isGameDebugEnabled()) return true
  const p = { x: bullet.x, y: bullet.y }
  wrap(p, WORLD_WIDTH, WORLD_HEIGHT, BULLET_RADIUS)
  drawBullet(ctx, { position: p, radius: BULLET_RADIUS, color: '#f33' }, camera)
  return true
}

function asteroidsWithUnwrap(asteroids, unwrapById) {
  return asteroids.map((a) => {
    const u = unwrapById.get(a.id)
    return u ? { ...a, x: u.x, y: u.y } : a
  })
}

function asteroidEffectiveRadius(a, asteroidMeta) {
  const meta = asteroidMeta.get(a.id)
  const r = a.radius ?? meta?.radius
  return r == null ? null : r + BULLET_RADIUS + HITBOX_DEBUG_PAD
}

function strokeCircle(ctx, camera, x, y, radius, withCrosshair, strokeStyle) {
  ctx.strokeStyle = strokeStyle
  camera.forEachVisibleCopy(x, y, radius, (sx, sy) => {
    ctx.beginPath()
    ctx.arc(sx, sy, radius, 0, Math.PI * 2)
    ctx.stroke()
    if (!withCrosshair) return
    ctx.beginPath()
    ctx.moveTo(sx - CROSSHAIR, sy)
    ctx.lineTo(sx + CROSSHAIR, sy)
    ctx.moveTo(sx, sy - CROSSHAIR)
    ctx.lineTo(sx, sy + CROSSHAIR)
    ctx.stroke()
  })
}

function strokeAsteroidHitRings(ctx, camera, asteroids, asteroidMeta, strokeStyle) {
  for (const a of asteroids) {
    const hitR = asteroidEffectiveRadius(a, asteroidMeta)
    if (hitR == null) continue
    strokeCircle(ctx, camera, a.x, a.y, hitR, false, strokeStyle)
  }
}

// Center → nose → short barrel past nose (same geometry as `GameWorld.spawnBulletFromShip`).
function strokeShipNoseBarrelLine(ctx, camera, cx, cy, rotationDeg, strokeStyle) {
  const nose = rotatePoint(
    { x: 0, y: -SHIP_NOSE_OFFSET },
    { x: 0, y: 0 },
    rotationDeg * Math.PI / 180
  )
  const len = Math.hypot(nose.x, nose.y) || 1
  const barrelLen = len + BULLET_RADIUS * 1.25
  const bx = (nose.x / len) * barrelLen
  const by = (nose.y / len) * barrelLen
  const reach = SHIP_RADIUS + Math.hypot(bx, by)
  ctx.strokeStyle = strokeStyle
  camera.forEachVisibleCopy(cx, cy, reach, (scx, scy) => {
    ctx.beginPath()
    ctx.moveTo(scx, scy)
    ctx.lineTo(scx + bx, scy + by)
    ctx.stroke()
  })
}

// Hitbox rings + ship nose lines when `isDebugEnabled`.
export function drawDebugOverlayIfEnabled(ctx, {
  view,
  asteroidsInterpOverlayLag = null,
  shipsInterpOverlayLag = null,
  asteroidMeta,
  camera,
  asteroidUnwrap,
  asteroidsClient,
  localShipHitboxClient
}) {
  if (!isDebugEnabled()) return

  const clientAsteroids = asteroidsWithUnwrap(asteroidsClient ?? view.asteroids, asteroidUnwrap)
  const interpViewAsteroids = asteroidsInterpOverlayLag ?? view.asteroids
  const interpShips = shipsInterpOverlayLag ?? view.ships

  ctx.save()
  ctx.lineWidth = LINE_WIDTH

  strokeAsteroidHitRings(ctx, camera, interpViewAsteroids, asteroidMeta, 'rgba(220,50,50,.42)')
  strokeAsteroidHitRings(ctx, camera, clientAsteroids, asteroidMeta, 'rgba(255,255,255,.72)')

  /**
   * Red = lagged interp sample (same clock as overlay asteroids/bullets). White = local prediction ship only.
   * Ships do not lerp x/y across death/respawn in {@link SnapshotInterpolator} so red should not slide corpse→spawn.
   */
  for (const s of interpShips) {
    if (!s.alive) continue
    strokeCircle(ctx, camera, s.x, s.y, SHIP_RADIUS + BULLET_RADIUS + HITBOX_DEBUG_PAD, true, 'rgba(220,50,50,.42)')
    if (s.rotation != null && Number.isFinite(s.rotation)) {
      strokeShipNoseBarrelLine(ctx, camera, s.x, s.y, s.rotation, 'rgba(255,120,120,.9)')
    }
  }

  const lc = localShipHitboxClient
  if (lc && Number.isFinite(lc.x) && Number.isFinite(lc.y)) {
    strokeCircle(ctx, camera, lc.x, lc.y, SHIP_RADIUS + BULLET_RADIUS + HITBOX_DEBUG_PAD, true, 'rgba(255,255,255,.72)')
  }

  ctx.restore()
}
